import { useTwttrLoad } from '@hooks/useTwitterEmbedPaste';
import {
  ContentsWrap,
  DetailContents,
} from '@pages/Posting/PostingDetail/sideType/DetailStyle';
import { replaceContents } from '@utils/replaceContents';
import React, { useCallback } from 'react';
import { sanitizeAndScopeHTML } from '@utils/cssScoping';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { searchValueState } from '@atom/search/search';
import { highlightHTMLText } from '@utils/highlightHTMLText';
import useWindowWidth from '@hooks/useWindowWidth';

type Props = {
  value: string | undefined;
  htmlValue?: string | undefined;
  isHtmlMode?: boolean;
  blockRightMouse?: boolean;
  style?: React.CSSProperties;
  onClick?: () => void;
  lineHeight?: number;
};
function QuillContents({
  value,
  htmlValue,
  isHtmlMode,
  blockRightMouse = false,
  style,
  onClick,
  lineHeight,
  ...props
}: Props) {
  useTwttrLoad(value || '');
  const navigate = useNavigate();
  const searchValue = useRecoilValue(searchValueState);
  const setSearchValue = useSetRecoilState(searchValueState);
  // 모바일에서는 #N 타임라인 칩 등 _blank 링크도 현재 페이지로 이동시켜
  // 삼성 인터넷 등 일부 브라우저의 세션 격리 회귀를 우회한다.
  const isMobile = useWindowWidth() < 480;

  // 내부 <a> / 해시태그 칩 클릭 핸들러
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // 해시태그 칩 클릭 → 검색 인풋에 태그 텍스트 삽입
      const hashtagChip = (e.target as HTMLElement).closest(
        '.timeline-hashtag-chip',
      );
      if (hashtagChip) {
        e.preventDefault();
        e.stopPropagation();
        const tag = hashtagChip.getAttribute('data-search-tag');
        if (tag) {
          setSearchValue(`#${tag}`);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        return;
      }

      const target = (e.target as HTMLElement).closest('a');
      if (!target) {
        onClick?.();
        return;
      }
      const href = target.getAttribute('href');
      if (!href) return;

      const targetAttr = target.getAttribute('target');

      // 같은 origin 내부 경로는 가능한 한 SPA 네비게이션으로 intercept.
      // - 데스크톱: target="_blank" 는 기본 동작(새 탭) 유지
      // - 모바일: target="_blank" 라도 현재 페이지로 이동시켜 세션 유지
      try {
        const url = new URL(href, window.location.origin);
        const isInternal = url.origin === window.location.origin;

        if (!isInternal) return; // 외부 링크는 기본 동작

        if (targetAttr === '_blank' && !isMobile) return; // 데스크톱은 새 탭 유지

        e.preventDefault();
        navigate(url.pathname + url.search + url.hash);
      } catch {
        // 유효하지 않은 URL이면 기본 동작 유지
      }
    },
    [navigate, onClick, setSearchValue, isMobile],
  );

  // 양쪽 소스에서 style 태그 여부 판단
  const hasStyleInHtml = !!(
    htmlValue && /<style[^>]*>[\s\S]*?<\/style>/i.test(htmlValue)
  );
  const hasStyleInValue = !!(
    value && /<style[^>]*>[\s\S]*?<\/style>/i.test(value)
  );

  // 스코프 처리된 HTML 생성
  const scopedHtmlFromHtmlValue = sanitizeAndScopeHTML(
    htmlValue || '',
    '.scoped-detail',
  );
  const scopedHtmlFromValue = sanitizeAndScopeHTML(
    value || '',
    '.scoped-detail',
  );

  // 래퍼 클래스: 어느 쪽이든 style 태그가 있으면 스코프 클래스 적용
  const wrapClassName =
    hasStyleInHtml || hasStyleInValue ? 'scoped-detail' : 'ql-editor';

  // 렌더링할 최종 HTML 결정
  const contentHTML = (() => {
    let html = '';
    if (isHtmlMode) html = scopedHtmlFromHtmlValue || '';
    else if (hasStyleInHtml) html = scopedHtmlFromHtmlValue || '';
    else if (hasStyleInValue) html = scopedHtmlFromValue || '';
    else html = value || htmlValue || '';

    // 기존 데이터 호환: 커스텀 이모지 내부의 <div>를 <span>으로 치환 (<p> 안에서 <div>는 불가)
    html = html.replace(
      /<div class="ql-custom-emoji-thumbnail"([^>]*)><\/div>/g,
      '<span class="ql-custom-emoji-thumbnail"$1></span>',
    );

    // 기존 데이터 호환: 인라인 스타일로 저장된 타임라인 번호 칩을 CSS 클래스 방식으로 변환
    html = html.replace(
      /<a([^>]*?)href="([^"]+)"([^>]*?)style="[^"]*background:\s*var\(--subColor\)[^"]*"([^>]*)>#(\d+)<\/a>/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="timeline-number-chip">#$5</a>',
    );

    // 기존 데이터 호환: target 속성 없이 저장된 timeline-number-chip 에도 새 탭 속성 주입
    html = html.replace(
      /<a\b(?![^>]*\btarget=)([^>]*?)class="timeline-number-chip"([^>]*)>/g,
      '<a$1class="timeline-number-chip" target="_blank" rel="noopener noreferrer"$2>',
    );

    return html;
  })();

  // 검색어 하이라이트 적용
  const finalHTML = searchValue
    ? highlightHTMLText(contentHTML, searchValue)
    : contentHTML;

  if (!finalHTML) return null;

  return (
    <ContentsWrap
      className={wrapClassName}
      blockMouse={blockRightMouse}
      style={style}
      onClick={handleClick}
      {...props}
    >
      <DetailContents
        dangerouslySetInnerHTML={{
          __html: finalHTML,
        }}
        style={{ lineHeight: lineHeight || 1.6 }}
      />
    </ContentsWrap>
  );
}

export default QuillContents;
